<template>
  <VaCard>
    <VaCardTitle class="flex justify-between">
      <h1 class="card-title text-secondary font-bold uppercase">Preacreditaciones diarias</h1>
    </VaCardTitle>
    <VaCardContent class="flex flex-col gap-1">
      <div class="flex flex-col gap-2">
        <div class="body-content">
          <VaDataTable
            v-if="showSearch"
            class="region-revenue-table"
            :columns="[
              { key: 'selected', label: ' ', align: 'center' },
              { key: 'id_viaje', label: 'ID Viaje', align: 'left' },
              { key: 'hora_solicitud', label: 'Hora solicitud', align: 'left' },
              { key: 'name', label: 'Nombre' },
              { key: 'RUT', label: 'RUT / Pasaporte', align: 'left' },
              { key: 'nave', label: 'Embarcación', align: 'left' },
              { key: 'rol', label: 'Rol', align: 'left' },
              //{ key: 'estado', label: 'Estado', align: 'left' },
              { key: 'actions', label: ' ', align: 'right' },
            ]"
            :items="showData"
            no-data-html="No hay preacreditaciones pendientes"
          >
            <template #cell(selected)="{ rowData }">
              <VaCheckbox v-model="selectedItemsEmitted" :array-value="rowData" />
            </template>
            <template #cell(id_viaje)="{ rowData }">
              {{ rowData.viajes.map((viaje) => viaje.id).join(', ') }}
            </template>
            <template #cell(name)="{ rowData }">
              {{
                rowData.solicitudes[0].tipo_objeto_2 == 'tripulante'
                  ? (rowData.user as ITripulante).nombre_completo
                  : (rowData.user as IArmador).nombre_representante
              }}
            </template>
            <template #cell(RUT)="{ rowData }">
              {{
                rowData.solicitudes[0].tipo_objeto_2 == 'tripulante'
                  ? (rowData.user as ITripulante).rut
                  : (rowData.user as IArmador).rut_representante
              }}
            </template>
            <template #cell(hora_solicitud)="{ rowData }">
              {{ showableDate(new Date(rowData.solicitudes[0].fecha_solicitud)) }}
            </template>
            <template #cell(nave)="{ rowData }">
              <div class="flex flex-col gap-1">
                <div
                  v-for="viaje in rowData.viajes.reduce((acc, viaje) => {
                    if (!acc.find((v) => v.embarcacionData.id === viaje.embarcacionData.id)) {
                      acc.push(viaje)
                    }
                    return acc
                  }, [] as ViajeEmbarcacion[])"
                  :key="viaje.id"
                  class="px-2 py-1 rounded"
                  :style="{ backgroundColor: getColor('primary-lighten-4'), color: 'white' }"
                >
                  {{ '(' + viaje.embarcacionData.id_lancha + ') ' + viaje.embarcacionData.nombre_embarcacion }}
                </div>
              </div>
            </template>
            <template #cell(rol)="{ rowData }">
              {{ rowData.roles.sort((a, b) => a.localeCompare(b)).join(', ') }}
            </template>
            <template #cell(estado)="{ rowData }">
              <VaChip
                v-if="rowData.solicitudes.some((solicitud) => solicitud.estado === RequestStatus.PENDING)"
                color="default"
                size="small"
                icon="pending"
              >
                Pendiente
              </VaChip>
              <VaChip
                v-else-if="rowData.solicitudes.some((solicitud) => solicitud.estado === RequestStatus.APPROVED)"
                color="success"
                size="small"
                icon="check"
              >
                Aprobado
              </VaChip>
              <VaChip
                v-else-if="rowData.solicitudes.some((solicitud) => solicitud.estado === RequestStatus.REJECTED)"
                color="danger"
                size="small"
                icon="close"
              >
                Rechazado
              </VaChip>
            </template>
          </VaDataTable>
          <Paginador v-if="fullData.length > pagination.perPage" :pagination="pagination" />
          <!-- Botones de accion -->
          <div v-if="showData.length > 0" class="flex gap-2">
            <VaButton
              :disabled="selectedItemsEmitted.length <= 0"
              size="small"
              icon-right="check"
              color="success"
              aria-label="Edit user"
              @click="aprobarSeleccionados()"
            >
              Aprobar
            </VaButton>
            <VaButton
              :disabled="selectedItemsEmitted.length <= 0"
              size="small"
              icon-right="close"
              color="danger"
              aria-label="Edit user"
              @click="rechazarSeleccionados()"
            >
              Rechazar
            </VaButton>
          </div>
        </div>
      </div>
    </VaCardContent>
  </VaCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, Ref, watch } from 'vue'
import VaTimelineItem from '../../../../components/va-timeline-item.vue'
import dataStorageManager, { BucketDefaultIDs } from '../../../../managers/data/DataStorageManager'
import dataHubServices from '../../../../services/data-hub-services/DataHubServices'
import { viajesHubName, ViajesHubPipelines } from '../../../../services/data-hub-services/ViajesHub'
import { IRequest, RequestObjectType, RequestStatus } from '../../../../services/api-services/RequestsServices'
import { ITripulante } from '../../../../services/api-services/TripulantesServices'
import { OperatedViaje } from '../../../../services/api-services/ViajesServices'
import { IEmbarcacion } from '../../../../services/api-services/EmbarcacionesServices'
import { useColors, useModal, useToast, VaButton } from 'vuestic-ui'
import { Viaje, getViajes } from '../../../../data/ViajesProperties'
import { Pagination } from '../../../../data/pages/general'
import { IArmador } from '../../../../services/api-services/ArmadoresServices'
import identityManager, { LoggedInUser } from '../../../../managers/identity/IdentityManager'
import {
  defaultIIncidencia,
  IIncidencia,
  IncidenceCode,
  IncidenceType,
} from '../../../../services/api-services/BitacoraServices'
import Paginador from '../../../../components/paginador/paginador.vue'

const { getColor } = useColors()

const selectedPeriod = ref('RUT') as Ref<'RUT' | 'Nombre'>
const periods = ['RUT', 'Nombre'].map((period) => ({ label: period, value: period }))
const searchQuery = ref('')
const showSearch = ref(true)
const selectedItemsEmitted = ref<TripulanteViajes[]>([])

const fullViajesList: Ref<OperatedViaje[]> = ref([])

const { init: notify } = useToast()
const { confirm } = useModal()

const pagination: Ref<Pagination> = ref({
  page: 1,
  perPage: 10,
  total: 0,
})

interface ViajeEmbarcacion extends OperatedViaje {
  embarcacionData: IEmbarcacion
  tripulanteRequest: IRequest
}

interface TripulanteViajes {
  solicitudes: IRequest[]
  viajes: ViajeEmbarcacion[]
  roles: string[]
  user: ITripulante | IArmador
}

const showData: Ref<TripulanteViajes[]> = ref([])
const fullData: Ref<TripulanteViajes[]> = ref([])

watch(
  [pagination, fullData],
  () => {
    showData.value = fullData.value.slice(
      pagination.value.perPage * (pagination.value.page - 1),
      pagination.value.perPage * pagination.value.page,
    )
  },
  { deep: true },
)

onMounted(() => {
  dataHubServices.subscribeToHub(viajesHubName, {
    subscriberID: 'operadorSolicitudesCredenciales_viajes',
    pipeline: ViajesHubPipelines.VIAJES_WITH_TRIPULANTES,
    callback: (data: OperatedViaje[]) => {
      fullViajesList.value = data
    },
  })
})

watch(fullViajesList, () => {
  const less12 = new Date(new Date().getTime() - 10 * 1000 * 60 * 60)
  const plus12 = new Date(new Date().getTime() + 24 * 1000 * 60 * 60)
  const completeViajes = fullViajesList.value
    .filter((v) => new Date(v.fecha_viaje).getTime() > less12.getTime())
    .filter((v) => new Date(v.fecha_viaje).getTime() < plus12.getTime())
    .map((viaje) => {
      const viajeAsociations: Array<{
        solicitud: IRequest
        viaje: OperatedViaje
        role: 'Patrón' | 'Tripulante' | 'Armador'
        user: ITripulante | IArmador
      }> = []

      if (viaje.armadorData) {
        const armador = dataStorageManager.searchDataInBucket<IArmador>(
          BucketDefaultIDs.ARMADORES,
          (a) => a.id.toString() === viaje.armadorData!.id_objeto_2!.toString(),
        )
        if (armador) {
          viajeAsociations.push({
            solicitud: viaje.armadorData,
            viaje: viaje,
            role: 'Armador',
            user: armador,
          })
        }
      }

      if (viaje.patronData) {
        viajeAsociations.push({
          solicitud: viaje.patronData.requestData!,
          viaje: viaje,
          role: 'Patrón',
          user: viaje.patronData,
        })
      }

      viaje.tripulantesData.forEach((tripulante) => {
        const tripulanteAsociation = viaje.asociationData.find(
          (a) =>
            a.tipo_objeto_2 === RequestObjectType.Tripulante && a.id_objeto_2?.toString() == tripulante.id.toString(),
        )
        if (tripulanteAsociation) {
          viajeAsociations.push({
            solicitud: tripulanteAsociation,
            viaje: viaje,
            role: 'Tripulante',
            user: tripulante,
          })
        }
      })

      return viajeAsociations
    })
    .flat()
    .filter((p) => p !== undefined)
    .filter((v) => v.solicitud.estado === RequestStatus.PENDING)

  fullData.value = completeViajes
    .reduce((acc, curr) => {
      const existingEntry = acc.find((entry) => {
        return entry.user.id.toString() === curr.user.id.toString()
      })
      if (existingEntry) {
        const roles = existingEntry.roles
        if (roles.indexOf(curr.role) === -1) {
          existingEntry.roles = roles.concat([curr.role]).sort()
        }
        existingEntry.solicitudes.push(curr.solicitud)
        existingEntry.viajes.push({
          ...curr.viaje,
          embarcacionData: curr.viaje.embarcacionData!,
          tripulanteRequest: curr.solicitud,
        })
      } else {
        acc.push({
          //selected: false,
          roles: [curr.role],
          user: curr.user,
          solicitudes: [curr.solicitud],
          viajes: [
            {
              ...curr.viaje,
              embarcacionData: curr.viaje.embarcacionData!,
              tripulanteRequest: curr.solicitud,
            },
          ],
        })
      }
      return acc
    }, [] as TripulanteViajes[])
    .sort((a, b) => {
      const lastASolicitudA = a.solicitudes.reduce((latest, solicitud) => {
        const solicitudDate = new Date(solicitud.fecha_solicitud).getTime()
        return solicitudDate > latest ? solicitudDate : latest
      }, 0)
      const lastASolicitudB = b.solicitudes.reduce((latest, solicitud) => {
        const solicitudDate = new Date(solicitud.fecha_solicitud).getTime()
        return solicitudDate > latest ? solicitudDate : latest
      }, 0)
      return lastASolicitudB - lastASolicitudA
    })
  pagination.value = {
    page: 1,
    perPage: pagination.value.perPage,
    total: fullData.value.length,
  }
})

// ISO string to dd/mm/aaaa hh:mm
function showableDate(date: Date) {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

async function aprobarSeleccionados() {
  const confirmed = await confirm({
    title: 'Aprobar tripulante',
    message: `¿Estás seguro de aprobar a los tripulantes seleccionados?`,
    okText: 'Sí, aprobar',
    cancelText: 'Cancelar',
  })
  if (confirmed) {
    const currentUser: LoggedInUser = identityManager.getUser()!

    for (const rowData of selectedItemsEmitted.value) {
      const preacreditacionPromises = rowData.solicitudes.map(async (solicitud, index) => {
        dataStorageManager.performOperation(BucketDefaultIDs.SOLICITUDES, 'updateRequest', {
          ...solicitud,
          revisor: currentUser.id_usuario,
          fecha_respuesta: new Date().toISOString(),
          estado: RequestStatus.APPROVED,
          fecha_caducidad: new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
          razon_rechazo: '',
        } as IRequest)

        const bitacoraData: IIncidencia = { ...defaultIIncidencia }
        bitacoraData.tipo_incidencia_id = IncidenceType.PREACREDITACION
        bitacoraData.codigo_incidencia = IncidenceCode.APROBACION
        bitacoraData.descripcion = `Preacreditación aprobada para viaje ID ${rowData.viajes[index].id} y tripulante ID ${rowData.solicitudes[index].id_objeto_2}`
        bitacoraData.id_objeto = rowData.user.id
        dataStorageManager.performOperation(BucketDefaultIDs.BITACORA, 'insertIncidencia', bitacoraData)
      })
      await Promise.all(preacreditacionPromises)
    }

    notify({
      message: `Solicitud aprobada correctamente.`,
      color: 'success',
    })
  }

  selectedItemsEmitted.value = []
}

async function rechazarSeleccionados() {
  const confirmed = await confirm({
    title: 'Rechazar tripulante',
    message: `¿Estás seguro de rechazar a los tripulantes seleccionados?`,
    okText: 'Sí, rechazar',
    cancelText: 'Cancelar',
  })
  if (confirmed) {
    const currentUser: LoggedInUser = identityManager.getUser()!

    for (const rowData of selectedItemsEmitted.value) {
      const preacreditacionPromises = rowData.solicitudes.map(async (solicitud, index) => {
        dataStorageManager.performOperation(BucketDefaultIDs.SOLICITUDES, 'updateRequest', {
          ...solicitud,
          revisor: currentUser.id_usuario,
          fecha_respuesta: new Date().toISOString(),
          estado: RequestStatus.REJECTED,
          fecha_caducidad: new Date().toISOString(),
          razon_rechazo: 'Rechazado por el operador.',
        } as IRequest)

        const bitacoraData: IIncidencia = { ...defaultIIncidencia }
        bitacoraData.tipo_incidencia_id = IncidenceType.PREACREDITACION
        bitacoraData.codigo_incidencia = IncidenceCode.RECHAZO
        bitacoraData.descripcion = `Preacreditación rechazada para viaje ID ${rowData.viajes[index].id} y tripulante ID ${rowData.solicitudes[index].id_objeto_2}`
        bitacoraData.id_objeto = rowData.user.id
        dataStorageManager.performOperation(BucketDefaultIDs.BITACORA, 'insertIncidencia', bitacoraData)
      })
      await Promise.all(preacreditacionPromises)
    }

    notify({
      message: `Solicitud rechazada correctamente.`,
      color: 'success',
    })
  }

  selectedItemsEmitted.value = []
}
</script>

<style lang="scss" scoped>
.region-revenue-table {
  ::v-deep(tbody) {
    tr {
      border-top: 1px solid var(--va-background-border);
    }
  }
}

.pers-content-container {
  max-height: 636px;
  height: 100%;
}

.body-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
}

.gapped-div {
  gap: 10px;
}

.padded-button {
  padding: 0 10px;
}
</style>
